/* Resume content — driven by translations via the <Trans> / t() helpers.
   Formatting (bold, italic, links) is preserved because translation strings
   may contain <b>/<i>/<a> which <Trans/> renders as real React nodes. */

const { AssetRail, Highlight, ResumeHeader, QRCode, Section, Card, Bullets, Grid2, DividerBlock, Row, preloadAssets, MuteProvider } = window;
const { I18nProvider, useTranslation, Trans, LanguagePills, PrintButton } = window;

/* QR + caption shown in the middle of the header. The URL each QR
   encodes is per-language (translations.js → print.qrUrl). The whole
   block is wrapped in an <a> so the saved PDF has a real clickable link
   annotation over both the QR and the caption — clicking either opens
   the same URL the QR resolves to. The label is stacked one word per
   line by splitting on spaces — keeps "View / Demos" tight next to the
   QR instead of trailing horizontally. */
function QRPromo() {
  const { t } = useTranslation();
  const url = t("print.qrUrl");
  const href = /^https?:\/\//.test(url) ? url : `https://${url}`;
  const words = t("print.qrLabel").split(/\s+/);
  return (
    <a
      className="qr-promo"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <QRCode value={url} />
      <span className="qr-label">
        {words.map((w, i) => <span key={i}>{w}</span>)}
      </span>
    </a>
  );
}

/* ===== Asset sets — captions are pulled via t() so they translate too. ===== */

function buildAssets(t) {
  // w/h are the real pixel dimensions of each file (ffprobe/sips). They give
  // the loading placeholder the exact aspect ratio, so the rail doesn't
  // snap-resize when the media's metadata arrives (the old 16:9 / 9:16 guess
  // was wrong for most of these). Update them if a file is re-exported.
  return {
    dosCavalos_app: [
      { src: "demos/research_farm_app.mp4", portrait: true, w: 590, h: 1278 },
    ],
    dosCavalos_network: [
      { src: "demos/security_camera.mp4", noAudio: true, caption: t("captions.securityCamera"), w: 1280, h: 720 },
    ],
    dosCavalos_twin: [
      { src: "demos/interior_design_renovation walkthrough.mp4", noAudio: true, caption: t("captions.twinmotion"), hideRestart: true, w: 1920, h: 1080 },
      { src: "demos/revit_model_pointcloud walkthrough.mp4", noAudio: true, caption: t("captions.revit"), hideRestart: true, w: 1204, h: 720 },
    ],
    justGo: [
      { src: "demos/grocery_delivery_app.mp4", portrait: true, w: 590, h: 1278 },
    ],
    gradeBook: [
      { src: "demos/grade_app.mp4", portrait: true, w: 886, h: 1920 },
    ],
    minesweeper: [
      { src: "demos/minesweeper.jpg.webp", caption: t("captions.minesweeper"), w: 770, h: 578 },
    ],
    minecraftRL: [
      { src: "demos/minecraft_rl_agent_playthrough.mp4", noAudio: true, caption: t("captions.minecraft1"), hideRestart: true, w: 1100, h: 700 },
      { src: "demos/minecraft_agent_input_demo.mp4", noAudio: true, caption: t("captions.minecraft2"), hideRestart: true, w: 1676, h: 720 },
    ],
    door: [
      { src: "demos/automatic_door_demo.mp4", noAudio: true, w: 1280, h: 720 },
    ],
    portrait: [
      { src: "demos/portrait.png", portrait: true, w: 431, h: 543 },
    ],
    // Roles under NDA — hover still works, but the rail shows a text note
    // instead of a demo.
    nda: [
      { type: "note", text: t("captions.ndaNote") },
    ],
  };
}

/* "Tools used: …" line shown under a work card's bullets. */
function ToolsLine({ i18nKey }) {
  const { t } = useTranslation();
  const tools = t(i18nKey);
  if (!tools) return null;
  return (
    <p className="card-tools">
      <span className="card-tools-label">{t("work.toolsLabel")}</span> {tools}
    </p>
  );
}

function ResumeBody() {
  const { t, lang } = useTranslation();
  // Re-bind ASSETS when language changes so captions translate. The arrays
  // are referentially new each rebuild; Highlight keeps its registration id
  // and updates the rail registry in place (ctx.updateHighlight), so the
  // RailSlides — and the <video> elements inside them — survive a language
  // switch with only the captions re-rendering.
  const ASSETS = React.useMemo(() => buildAssets(t), [lang, t]);

  return (
    <MuteProvider>
    <AssetRail>
      <ResumeHeader
        left={<>
          <Highlight assets={ASSETS.portrait}>
            <div className="name">{t("header.name")}</div>
            <div className="subtitle">{t("header.subtitle")}</div>
          </Highlight>
        </>}
        middle={<QRPromo />}
        right={<>
          <div><a href={`mailto:${t("header.email")}`}>{t("header.email")}</a></div>
          <div><a href={`tel:${t("header.phone").replace(/[^\d+]/g, "")}`}>{t("header.phone")}</a></div>
          <div>{t("header.location")}</div>
          <div>{t("header.authorization")}</div>
        </>}
      />

      <Section title={t("sections.work")}>
        <Highlight assets={ASSETS.nda}>
          <Card
            left={t("work.orangeLogicAI.title")}
            right={<Trans i18nKey="work.orangeLogicAI.meta" />}
          >
            <Bullets>
              <Trans i18nKey="work.orangeLogicAI.b1" />
              <Trans i18nKey="work.orangeLogicAI.b2" />
            </Bullets>
            <ToolsLine i18nKey="work.orangeLogicAI.tools" />
          </Card>
        </Highlight>

        <Card
          left={t("work.dosCavalos.title")}
          right={<Trans i18nKey="work.dosCavalos.meta" />}
        >
          <Bullets>
            <Highlight assets={ASSETS.dosCavalos_app}><Trans i18nKey="work.dosCavalos.b1" /></Highlight>
            <Highlight assets={ASSETS.dosCavalos_network}><Trans i18nKey="work.dosCavalos.b2" /></Highlight>
            <Highlight assets={ASSETS.dosCavalos_twin}><Trans i18nKey="work.dosCavalos.b3" /></Highlight>
          </Bullets>
          <ToolsLine i18nKey="work.dosCavalos.tools" />
        </Card>

        <Highlight assets={ASSETS.justGo}>
          <Card
            left={t("work.justGo.title")}
            right={<Trans i18nKey="work.justGo.meta_pre" />}
          >
            <Bullets>
              <Trans i18nKey="work.justGo.b1" />
              <Trans i18nKey="work.justGo.b2" />
              <Trans i18nKey="work.justGo.b3" />
            </Bullets>
            <ToolsLine i18nKey="work.justGo.tools" />
          </Card>
        </Highlight>

        <Highlight assets={ASSETS.gradeBook}>
          <Card
            left={t("work.gradeBook.title")}
            right={<Trans i18nKey="work.gradeBook.meta" />}
          >
            <Bullets>
              <Trans i18nKey="work.gradeBook.b1" />
              <Trans i18nKey="work.gradeBook.b2" />
            </Bullets>
            <ToolsLine i18nKey="work.gradeBook.tools" />
          </Card>
        </Highlight>

        <Highlight assets={ASSETS.nda}>
          <Card
            left={t("work.orangeLogic.title")}
            right={<Trans i18nKey="work.orangeLogic.meta" />}
          >
            <Bullets>
              <Trans i18nKey="work.orangeLogic.b2" />
              <Trans i18nKey="work.orangeLogic.b3" />
            </Bullets>
            <ToolsLine i18nKey="work.orangeLogic.tools" />
          </Card>
        </Highlight>
      </Section>

      <Section title={t("sections.projects")}>
        <Highlight assets={ASSETS.minesweeper}>
          <Card left={t("projects.minesweeper.title")} meta={t("projects.minesweeper.meta")}>
            <Bullets>
              <Trans i18nKey="projects.minesweeper.b1" />
              <Trans i18nKey="projects.minesweeper.b2" />
            </Bullets>
          </Card>
        </Highlight>

        <Highlight assets={ASSETS.minecraftRL}>
          <Card left={t("projects.minecraft.title")} meta={t("projects.minecraft.meta")}>
            <Bullets>
              <Trans i18nKey="projects.minecraft.b1" />
            </Bullets>
          </Card>
        </Highlight>

        {/* print-hidden: keep the door project on the site but out of the PDF */}
        <Highlight assets={ASSETS.door} className="print-hidden">
          <Card left={t("projects.door.title")} meta={t("projects.door.meta")}>
            <Bullets>
              <Trans i18nKey="projects.door.b1" />
            </Bullets>
            <ToolsLine i18nKey="projects.door.tools" />
          </Card>
        </Highlight>
      </Section>

      <DividerBlock>
        <Row
          left={<Trans i18nKey="footer.eduLeft" />}
          right={<Trans i18nKey="footer.eduRight" />}
        />
        <p><Trans i18nKey="footer.eduLine" /></p>
        <p><Trans i18nKey="footer.languages" /></p>
        <p><Trans i18nKey="footer.interests" /></p>
      </DividerBlock>
    </AssetRail>
    </MuteProvider>
  );
}

function IntroScreen() {
  const { t } = useTranslation();
  const badges = t("intro.badges") || [];
  const flickStar = (event) => {
    const mark = event.currentTarget.querySelector(".intro-badge-mark");
    if (!mark || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const state = mark.spinState || { angle: 0, velocity: 0, rafId: 0, lastTime: 0 };
    state.velocity = Math.min(state.velocity + 0.14, 0.24);
    mark.dataset.flicking = "true";

    const step = (time) => {
      const delta = state.lastTime ? Math.min(time - state.lastTime, 32) : 16;
      state.lastTime = time;
      state.angle = (state.angle + state.velocity * delta) % 360;
      state.velocity *= Math.exp(-delta / 1400);
      mark.style.setProperty("--badge-flick-angle", `${state.angle}deg`);

      if (state.velocity > 0.006) {
        state.rafId = window.requestAnimationFrame(step);
        return;
      }

      state.velocity = 0;
      state.rafId = 0;
      state.lastTime = 0;
      delete mark.dataset.flicking;
    };

    if (!state.rafId) {
      state.rafId = window.requestAnimationFrame(step);
    }
    mark.spinState = state;
  };
  return (
    <section className="intro-screen" aria-labelledby="intro-name">
      <div className="intro-copy">
        <h1 className="intro-name" id="intro-name">{t("header.name")} · {t("intro.age")}</h1>
        <p className="intro-sentence"><Trans i18nKey="summary.positioning" /></p>
        <div className="intro-badges" aria-label={t("intro.badgeLabel")}>
          <span className="intro-badge" onMouseEnter={flickStar}>
            <span className="intro-badge-mark" aria-hidden="true">
              <span className="intro-badge-star">✦</span>
            </span>
            <span className="intro-stat-line">
              {badges.join(", ")}
              <span className="intro-stats-note">{t("intro.statsNote")}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

/* First-visit onboarding hint (desktop). When the resume first comes into
   view, the lower part of the viewport fogs to white and a message with an
   up-arrow points at the paragraphs, explaining that hovering a paragraph
   plays a video demo. Dismisses for good on the first demo shown, the next
   scroll, or any click/tap. Never shown on mobile (or in the PDF). */
function DemoHint({ active }) {
  const { t } = useTranslation();
  // Desktop = real hover pointer on a wide screen; everything else (touch
  // phones/tablets) gets the tap copy. Both fire `resume:demo-shown` on the
  // first demo, so dismissal works the same either way.
  const isDesktop = React.useRef(
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (min-width: 821px)").matches
  );
  const [show, setShow] = React.useState(false);
  const doneRef = React.useRef(false);

  // Show as soon as the resume is in view — present right away, not after a
  // delay. (The CSS opacity transition still fades it in.)
  React.useEffect(() => {
    if (doneRef.current || !active) return;
    setShow(true);
  }, [active]);

  // Dismiss for good ONLY when the user opens a demo (hover on desktop, tap on
  // mobile) — not on scroll or stray click.
  React.useEffect(() => {
    if (!show) return;
    const dismiss = () => { doneRef.current = true; setShow(false); };
    window.addEventListener("resume:demo-shown", dismiss);
    return () => window.removeEventListener("resume:demo-shown", dismiss);
  }, [show]);

  return (
    <div className={"demo-hint" + (show ? " demo-hint--show" : "")} aria-hidden="true">
      <div className="demo-hint-fog" />
      <div className="demo-hint-msg">
        <svg className="demo-hint-arrow" width="26" height="34" viewBox="0 0 26 34" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13 32V4" />
          <path d="M4 13 13 4l9 9" />
        </svg>
        <span className="demo-hint-text">{t(isDesktop.current ? "desktopHint" : "mobileHint")}</span>
      </div>
    </div>
  );
}

function shouldShowResumeInitially() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("resume") === "1" || window.location.hash === "#resume";
  } catch (e) {
    return false;
  }
}

function App() {
  const showResumeOnly = shouldShowResumeInitially();
  const [resumeFocused, setResumeFocused] = React.useState(false);

  // Tap-to-focus: while the page is still a tilted preview (landing, not yet
  // scrolled into), a click anywhere on it scrolls the resume into focus —
  // same target the scroll-snap uses, so it feels identical to scrolling.
  // We intercept the click (preventDefault) so taps on the angled links don't
  // navigate before the page is actually in focus. Once resume-active, this
  // no-ops and the page behaves normally.
  const focusResume = React.useCallback((event) => {
    if (document.body.classList.contains("resume-active")) return;
    // Only intercept while the resume is still a peek at the bottom of the
    // screen. On mobile there's no scroll-snap, so the user can rest mid-way
    // — in a long "dead zone" where the resume is scrolled into reading range
    // but `resume-active` hasn't flipped yet (it only flips once the top-bar
    // reaches the very top). A tap there must pass through to inner handlers
    // (e.g. opening a demo), NOT re-run the focus scroll and yank back up.
    // We key off the page's on-screen position rather than window.scrollY:
    // scrollY is unreliable on iOS (the root can become a scroll container),
    // but getBoundingClientRect is viewport-relative and always correct. The
    // untouched peek sits ~80svh down; once its top rises into the upper
    // two-thirds of the viewport the user is reading, so let the tap through.
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.66) return;
    event.preventDefault();
    // Capture-phase stop so the tap doesn't also reach inner handlers (e.g.
    // a Highlight row's mobile tap, which would pop a demo drawer over the
    // page we're focusing). Once focused, this returns early above and taps
    // pass through normally.
    event.stopPropagation();
    const bar = document.querySelector(".resume-top-bar");
    if (!bar) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    bar.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, []);

  React.useEffect(() => {
    document.body.classList.toggle("landing-active", !showResumeOnly);
    return () => {
      document.body.classList.remove("landing-active");
      document.body.classList.remove("resume-active");
    };
  }, [showResumeOnly]);

  React.useEffect(() => {
    if (showResumeOnly) {
      document.body.classList.remove("resume-active");
      return;
    }

    let frame = 0;
    const syncResumeActive = () => {
      frame = 0;
      const resumeTopBar = document.querySelector(".resume-top-bar");
      if (!resumeTopBar) return;
      // Hysteresis: activate when the top bar reaches the top (<=120px), but
      // only deactivate once it's back past ~45% of the viewport. Without the
      // gap, the class flips at a single threshold and re-engages mandatory
      // scroll-snap mid-gesture, which can oscillate (snap yanks the page
      // back across the threshold, re-flipping the class). 45% also puts the
      // release past the midpoint between the two snap targets (~80svh
      // apart), so when snap re-engages it resolves UP to the intro instead
      // of yanking back down to the resume.
      const top = resumeTopBar.getBoundingClientRect().top;
      const wasActive = document.body.classList.contains("resume-active");
      const active = top <= (wasActive ? window.innerHeight * 0.45 : 120);
      document.body.classList.toggle("resume-active", active);
      setResumeFocused(active);
    };
    const requestSync = () => {
      if (frame) return;
      frame = requestAnimationFrame(syncResumeActive);
    };

    syncResumeActive();
    window.addEventListener("scroll", requestSync, { passive: true });
    window.addEventListener("resize", requestSync);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", requestSync);
      window.removeEventListener("resize", requestSync);
      document.body.classList.remove("resume-active");
    };
  }, [showResumeOnly]);

  return (
    <I18nProvider>
      {showResumeOnly ? (
        <>
          <div className="top-bar">
            <LanguagePills />
            <PrintButton />
          </div>
          <main className="page">
            <ResumeBody />
            <DemoHint active={showResumeOnly || resumeFocused} />
          </main>
        </>
      ) : (
        <>
          <div className="top-bar intro-top-bar">
            <LanguagePills />
          </div>
          <div className="landing-stage">
            <IntroScreen />
            <div className="top-bar resume-top-bar">
              <LanguagePills />
              <PrintButton />
            </div>
          </div>
          <main className="page resume-preview" id="resume" onClickCapture={focusResume}>
            <ResumeBody />
            <DemoHint active={showResumeOnly || resumeFocused} />
          </main>
        </>
      )}
    </I18nProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

// Preload every asset referenced so first-hover playback is instant.
// (Uses default English captions dict — preload only cares about src.)
const _preloadT = (key) => {
  const en = (window.TRANSLATIONS && window.TRANSLATIONS.en) || {};
  return key.split(".").reduce((a, k) => (a == null ? a : a[k]), en) || "";
};
preloadAssets(buildAssets(_preloadT));
